import React, { useEffect, useRef, useState } from "react";
import "../../../styles/components/Slices/SectionMonet.scss";
import {
  EncordCheck2,
  FeatureBullet,
  TableDaliDashIcon,
} from "../../../assets/icons/NavbarIcons/Icons";
import useWindowWidth from "../../../hooks/useBetterMediaQuery";
import CustomButton from "../../CustomButton";
import ToolTipText from "../../pricing_page/TooltipText";
import { CloseFeature } from "../../../assets/icons/closeFeatures";
import { ShowFeature } from "../../../assets/icons/showFeatures";
import xss from "xss";

export const TableDali = ({ slice, location, visibleFraction = 0.5 }) => {
  const [maxHeight, setMaxHeight] = useState("0px");
  const [openAnnotateFeature, setOpenAnnotateFeature] = useState(false);
  const sectionRef = useRef(null);
  const headerAboveRef = useRef(null);
  const isDesktop = useWindowWidth(1023);
  const isTablet = useWindowWidth(640);
  const isMobile = useWindowWidth(320);
  const [isSticky, setIsSticky] = useState(false);

  const toggleAnnotateFeature = () => {
    setOpenAnnotateFeature((prev) => !prev);
  };

  useEffect(() => {
    if (sectionRef.current) {
      const scrollHeight = sectionRef.current.scrollHeight;
      const shouldCollapse = slice?.primary?.show_more_features;

      if (shouldCollapse) {
        const newHeight = openAnnotateFeature
          ? scrollHeight
          : isDesktop
            ? scrollHeight * visibleFraction?.desktop
            : isTablet
              ? scrollHeight * visibleFraction?.tablet
              : scrollHeight * visibleFraction?.mobile;
        setMaxHeight(`${newHeight}px`);
      } else {
        setMaxHeight("none");
      }
    }
  }, [
    openAnnotateFeature,
    visibleFraction,
    slice?.primary?.show_more_features,
  ]);
  useEffect(() => {
    const handleScroll = () => {
      if (!headerAboveRef.current) return;

      const { top } = headerAboveRef.current.getBoundingClientRect();
      setIsSticky(top <= 210); // 189 is your sticky top offset
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {slice?.items[0]?.section_header_title?.text && (
        <>
          <div ref={headerAboveRef} className="h-0" />
          <div
            className={`flex flex-col-reverse lg:flex-row items-center justify-between lg:px-6 px-1.5 sticky top-[210px] ${
              isSticky ? "bg-[#FAFAFA] z-10 border-b border-[#A8A8A8]" : "bg-transparent z-0 border-b border-[#A8A8A8]"
            }`}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: xss(slice.items[0].section_header_title.html),
              }}
              className={`text-2xl sm:text-2.5xl pb-3 pt-6 tracking-tightest_5 sm:tracking-tightest_6 font-manrope font-semibold text-gray-2700`}
            />
          </div>
        </>
      )}

      <section
        ref={sectionRef}
        style={{
          maxHeight,
          overflow: "hidden",
          transition: "max-height 0.5s ease",
          opacity: 1,
        }}
      >
        <div>
          {slice?.items?.map((value, index, array) => {
            const nextItemHasHeader =
              array[index + 1]?.section_header_title?.text;
            const isLastItem = index === array.length - 1;
            return index !== 0 ? (
              <div
                key={index}
                className={`
                  ${
                    nextItemHasHeader
                      ? ""
                      : value.section_header_title?.text
                        ? "border-b border-[#A8A8A8]"
                        : "border-b border-[#E6E6E6]"
                  }
                  ${
                    !value?.section_header_title?.text &&
                    !value?.section_subtitle?.text
                      ? "hover:bg-[#f5f5f5] px-4 lg:pl-9 lg:pr-16 py-3"
                      : "px-1.5 lg:pl-6 py-3"
                  }
                `}
              >
                {value?.section_header_title?.text && (
                  <div
                    dangerouslySetInnerHTML={{
                        __html: xss(value.section_header_title.html),
                    }}
                    className="text-2xl sm:text-2.5xl tracking-tightest_5 text-center lg:text-left sm:tracking-tightest_6 font-manrope font-semibold text-gray-2700"
                  />
                )}
                {value?.section_subtitle?.text && (
                  <p className="text-xl sm:text-2xl tracking-tightest sm:tracking-tightest_5 lg:text-left text-center font-manrope font-semibold text-gray-2700">
                    {value?.section_subtitle?.text}
                  </p>
                )}
                <div className="flex gap-3">
                  {isDesktop && value?.feature_name?.text && <FeatureBullet />}
                  {value?.feature_name?.text && (
                    <div className="flex flex-col gap-1.5 lg:gap-0 lg:flex-row justify-between w-full">
                      <p className="tracking-tight_1 lg:text-left text-center lg:max-w-[200px] text-base sm:text-lg xl:max-w-[570px] font-normal text-gray-2500 sm:-tracking-[0.32px]">
                        <span>{value?.feature_name?.text}</span>
                        <span className="whitespace-nowrap">
                          {!!value?.feature_name_tooltip?.text && (
                            <span className="inline-flex ml-1">
                              <ToolTipText
                                text={value?.feature_name_tooltip?.text}
                                overlayStyle={
                                  value?.feature_name_tooltip?.text?.length <=
                                  33
                                    ? "tooltip_overlay_custom_class_single_line"
                                    : "tooltip_overlay_custom_class"
                                }
                              />
                            </span>
                          )}
                        </span>
                      </p>
                      <div className="flex justify-between items-center lg:gap-[116px] sm:px-6 md:px-0 w-auto">
                        <div className="flex justify-center">
                          {value?.starter_column?.text === "check" ? (
                            <EncordCheck2 fillColor="#434343" />
                          ) : value?.starter_column?.text ? (
                            <p className="text-base md:min-w-[100px] md:mx-4 lg:mx-0 text-center font-inter font-normal text-gray-2500 -tracking-[0.32px]">
                              {value.starter_column.text}
                            </p>
                          ) : (
                            !value?.section_header_title?.text && (
                              <TableDaliDashIcon />
                            )
                          )}
                        </div>
                        <div className="flex justify-center md:px-0 lg:min-w-[100px]">
                          {value?.team_column?.text === "check" ? (
                            <EncordCheck2 fillColor="#10239E" />
                          ) : value?.team_column?.text ? (
                            <p className="text-base md:min-w-[100px] md:mx-4 lg:mx-0 text-center font-inter font-normal text-blue-1300 -tracking-[0.32px]">
                              {value.team_column.text}
                            </p>
                          ) : (
                            !value?.section_header_title?.text && (
                              <TableDaliDashIcon />
                            )
                          )}
                        </div>
                        <div className="flex justify-center md:px-0 lg:min-w-[100px]">
                          {value?.enterprise_column?.text === "check" ? (
                            <EncordCheck2 fillColor="#5E44FF" />
                          ) : value?.enterprise_column?.text ? (
                            <p className="text-base md:min-w-[100px] md:mx-4 lg:mx-0 text-center font-inter font-normal text-purple-1500 -tracking-[0.32px]">
                              {value?.enterprise_column?.text}
                            </p>
                          ) : (
                            !value?.section_header_title?.text && (
                              <TableDaliDashIcon />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null;
          })}
        </div>
      </section>
      {slice?.primary?.show_more_features &&
        (openAnnotateFeature ? (
          <CustomButton
            text="Show less"
            icon={<CloseFeature />}
            buttonClass="text-gray-2500 my-5 hover:text-gray-2700 bg-gray-1600 text-base mx-auto"
            direction="top"
            onClick={toggleAnnotateFeature}
          />
        ) : (
          <CustomButton
            text="Show more features"
            icon={<ShowFeature />}
            buttonClass="text-gray-2500 my-5 hover:text-gray-2700 bg-gray-1600 text-base mx-auto"
            direction="bottom"
            onClick={toggleAnnotateFeature}
          />
        ))}
    </>
  );
};
